import {
	cloneElement,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
	type FocusEventHandler,
	type MouseEventHandler,
	type PointerEventHandler,
	type ReactElement,
} from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

/**
 * Y-10: Drop-in replacement for native title= attributes.
 *
 * Desktop: shows tooltip on hover/focus (same as title=).
 * Touch:   shows tooltip on tap; tap elsewhere or the element again to dismiss.
 *
 * Usage:
 *   <MobileTooltip title={tooltipText}>
 *     <span className="skill">spd</span>
 *   </MobileTooltip>
 *
 * The child must be a DOM element or a ref-forwarding component so that
 * react-bootstrap's OverlayTrigger can attach its ref + event listeners.
 * Native elements (<span>, <div>, <button>, …) always qualify.
 */

type Props = {
	title: string | undefined;
	/** Must be a single React element (not a string or Fragment). */
	children: ReactElement;
	/** Fires before the tooltip opens/closes, like OverlayTrigger's onToggle. */
	onToggle?: (nextShow: boolean) => void;
	placement?: "top" | "bottom" | "left" | "right";
};

export const MobileTooltip = ({ title, children, onToggle, placement }: Props) => {
	const id = useId();
	const [show, setShow] = useState(false);
	const openedByHoverRef = useRef(false);
	const showRef = useRef(false);
	const showAtPointerDownRef = useRef(false);
	const requestedPlacement = placement ?? "top";
	const handleToggle = useCallback(
		(nextShow: boolean) => {
			if (showRef.current === nextShow) {
				return;
			}
			showRef.current = nextShow;
			setShow(nextShow);
			onToggle?.(nextShow);
		},
		[onToggle],
	);

	useEffect(() => {
		if (!show) {
			return;
		}

		const handleDocumentPointerDown = (event: PointerEvent) => {
			const trigger = document.querySelector(
				`[data-mobile-tooltip-instance="${id}"]`,
			);
			const tooltip = document.getElementById(id);
			if (
				event.target instanceof Node &&
				(trigger?.contains(event.target) || tooltip?.contains(event.target))
			) {
				return;
			}
			handleToggle(false);
		};
		document.addEventListener("pointerdown", handleDocumentPointerDown);
		return () => {
			document.removeEventListener("pointerdown", handleDocumentPointerDown);
		};
	}, [handleToggle, id, show]);

	useEffect(() => {
		if (!show) {
			return;
		}

		const handleFocusIn = (event: FocusEvent) => {
			const trigger = document.querySelector(
				`[data-mobile-tooltip-instance="${id}"]`,
			);
			if (event.target instanceof Node && trigger?.contains(event.target)) {
				return;
			}
			handleToggle(false);
		};
		document.addEventListener("focusin", handleFocusIn);
		return () => {
			document.removeEventListener("focusin", handleFocusIn);
		};
	}, [handleToggle, id, show]);

	useEffect(() => {
		if (!show) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				handleToggle(false);
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [handleToggle, show]);

	useEffect(() => {
		if (!title && show) {
			handleToggle(false);
		}
	}, [handleToggle, show, title]);

	if (!title) {
		return children;
	}

	const nativeInteractive =
		typeof children.type === "string" &&
		["a", "button", "input", "select", "summary", "textarea"].includes(children.type);
	const childProps = children.props as {
		onBlur?: FocusEventHandler<HTMLElement>;
		onClick?: MouseEventHandler<HTMLElement>;
		onFocus?: FocusEventHandler<HTMLElement>;
		onMouseEnter?: MouseEventHandler<HTMLElement>;
		onMouseLeave?: MouseEventHandler<HTMLElement>;
		onPointerDownCapture?: PointerEventHandler<HTMLElement>;
		tabIndex?: number;
	};
	const handleBlur: FocusEventHandler<HTMLElement> = (event) => {
		handleToggle(false);
		childProps.onBlur?.(event);
	};
	const handleClick: MouseEventHandler<HTMLElement> = (event) => {
		handleToggle(event.detail > 0 ? !showAtPointerDownRef.current : !showRef.current);
		childProps.onClick?.(event);
	};
	const handleFocus: FocusEventHandler<HTMLElement> = (event) => {
		openedByHoverRef.current = false;
		handleToggle(true);
		childProps.onFocus?.(event);
	};
	const handleMouseEnter: MouseEventHandler<HTMLElement> = (event) => {
		openedByHoverRef.current = !showRef.current;
		handleToggle(true);
		childProps.onMouseEnter?.(event);
	};
	const handleMouseLeave: MouseEventHandler<HTMLElement> = (event) => {
		if (openedByHoverRef.current) {
			handleToggle(false);
		}
		openedByHoverRef.current = false;
		childProps.onMouseLeave?.(event);
	};
	const handlePointerDownCapture: PointerEventHandler<HTMLElement> = (event) => {
		showAtPointerDownRef.current = showRef.current;
		if (openedByHoverRef.current) {
			showAtPointerDownRef.current = false;
		}
		openedByHoverRef.current = false;
		childProps.onPointerDownCapture?.(event);
	};
	const trigger = cloneElement(children as ReactElement<Record<string, unknown>>, {
		"data-mobile-tooltip-instance": id,
		"data-mobile-tooltip-open": show ? "true" : "false",
		"data-mobile-tooltip-placement-requested": requestedPlacement,
		"data-mobile-tooltip-trigger": "",
		onBlur: handleBlur,
		onClick: handleClick,
		onFocus: handleFocus,
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
		onPointerDownCapture: handlePointerDownCapture,
		...(!nativeInteractive && childProps.tabIndex === undefined ? { tabIndex: 0 } : {}),
	});

	return (
		<OverlayTrigger
			flip
			trigger={[]}
			onToggle={handleToggle}
			placement={requestedPlacement}
			show={show}
			overlay={
				<Tooltip
					data-mobile-tooltip=""
					data-mobile-tooltip-placement-requested={requestedPlacement}
					id={id}
				>
					{title}
				</Tooltip>
			}
		>
			{trigger}
		</OverlayTrigger>
	);
};
